// Phase 15 M4 — Stripe PaymentElement checkout for FIXED-price sales.
//
// Lifecycle (PRD §M4):
//   1. Mount: POST /marketplace/sales/:id/reserve to get a Stripe client_secret
//      (the backend also creates the Documenso contract + commission split
//      server-side; the response carries `payment_intent_id`, `client_secret`,
//      `amount_cents`). Errors mapped to specific UI states — most importantly
//      a 409 "stripe customer" signal redirects to /buyer-confirm so the buyer
//      finishes the first-purchase capture before retrying.
//   2. Reserve success → mount `<Elements>` with the secret + render
//      `<PaymentElement />`. Single "Confirmar pagamento" button calls
//      `stripe.confirmPayment` with `return_url` pointing back here with
//      `?payment=ok&payment_intent=...`.
//   3. Stripe redirects back; the post-payment branch shows "pagamento
//      procesado" + an M5 hook to the signing step. M5 extends this page
//      with the Documenso pestana-nova workflow + polling.
//
// CSP: nginx (Phase 19) gets `script-src https://js.stripe.com`, `frame-src
// https://*.stripe.com https://m.stripe.network`, and `connect-src
// https://api.stripe.com` so Stripe.js loads without violations.

import axios from 'axios';
import { Lock } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';

import type { CheckoutIntentResponse, MarketplaceSaleDetail } from '@leyrochain/shared';

import InfoCallout from '../components/InfoCallout';
import { getPaymentStatus, getSale, getSigningUrl, reserveSale } from '../api/marketplace';

type ReserveErrorKind =
  | 'kyc_pending'
  | 'attestations_missing'
  // Lote V-2 (F-022) — DDS EUDR pendente do vendedor; flow distinto de
  // attestations_missing (que cobre os 4 checks declarativos do LIST).
  | 'eudr_dds_required'
  | 'buyer_confirm_required'
  | 'sale_unavailable'
  | 'billing_unavailable'
  | 'auth_required'
  | 'generic';

interface ReserveError {
  kind: ReserveErrorKind;
  message: string;
}

function classifyReserveError(err: unknown): ReserveError {
  if (!axios.isAxiosError(err)) {
    return { kind: 'generic', message: 'Erro inesperado. Téntao máis tarde.' };
  }
  const status = err.response?.status;
  const data = err.response?.data as { error?: string; reason?: string } | undefined;
  const reason = data?.reason ?? '';
  if (status === 503) {
    return {
      kind: 'billing_unavailable',
      message: 'O pagamento está temporalmente fora de servizo. Téntao en breve.',
    };
  }
  if (status === 401) {
    return { kind: 'auth_required', message: 'Inicia sesión para continuar.' };
  }
  if (status === 403 && /kyc/i.test(reason)) {
    return {
      kind: 'kyc_pending',
      message: 'Completa o KYC dende a app móbil para poder pagar.',
    };
  }
  if (status === 409 && /attestations_missing/.test(reason)) {
    return {
      kind: 'attestations_missing',
      message:
        'O vendedor aínda non rematou de configurar a venda (faltan declaracións obrigatorias). Contacta co vendedor.',
    };
  }
  // Lote V-2 (F-022) — DDS EUDR é un fluxo independente; mensaxe accionable.
  if (status === 409 && /eudr_dds_required/.test(reason)) {
    return {
      kind: 'eudr_dds_required',
      message:
        'O vendedor non publicou aínda a declaración EUDR para este animal. Contacta co vendedor para que a complete.',
    };
  }
  // Lote V-2 (F-026/F-027) — primeira compra do BUYER. Tras dar a este
  // motivo, redirixímoslle a /buyer-confirm onde se crea Stripe customer +
  // SEPA mandate; cando vuelva, /reserve xa pasa. O patrón legacy
  // "stripe customer|complete profile" mantense para clientes vellos.
  if (status === 409 && /buyer_setup_required|stripe customer|complete profile/i.test(reason)) {
    return {
      kind: 'buyer_confirm_required',
      message: 'Antes da primeira compra debes completar os datos fiscais.',
    };
  }
  if (status === 409) {
    return {
      kind: 'sale_unavailable',
      message: reason
        ? `Non se puido reservar: ${reason}`
        : 'Este animal xa non está dispoñible para reservar.',
    };
  }
  return { kind: 'generic', message: reason || 'Non se puido iniciar a reserva.' };
}

/**
 * Resolves the Stripe publishable key.
 *
 * Reads from `import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY` at runtime.
 * Tests inject a value via `__setPublishableKeyForTests` because Vitest's
 * `vi.stubEnv` doesn't always reach the Vite-flavoured `import.meta.env`
 * proxy reliably across versions.
 */
let publishableKeyOverride: string | null | undefined;
export function __setPublishableKeyForTests(value: string | null | undefined): void {
  publishableKeyOverride = value;
}
function getPublishableKey(): string | null {
  if (publishableKeyOverride !== undefined) return publishableKeyOverride;
  const value = (import.meta as { env?: { VITE_STRIPE_PUBLISHABLE_KEY?: string } }).env
    ?.VITE_STRIPE_PUBLISHABLE_KEY;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

// loadStripe is memoised at module scope so we don't refire the Stripe.js
// download on every CheckoutPage mount. Each unique publishable key gets its
// own cached Promise (in practice there's only one — dev or prod).
const stripePromiseCache = new Map<string, Promise<Stripe | null>>();
export function getStripePromise(publishableKey: string): Promise<Stripe | null> {
  const cached = stripePromiseCache.get(publishableKey);
  if (cached) return cached;
  const fresh = loadStripe(publishableKey);
  stripePromiseCache.set(publishableKey, fresh);
  return fresh;
}

function formatEuros(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

// Lote V-2 (F-029) — Mapper de erros de Stripe.js a copia accionable en
// galego. `confirmPayment` devolve un `StripeError` con `type` +
// opcionalmente `code`; ao invocala antes de que o `<PaymentElement>` se
// monte, Stripe lanza un erro `invalid_request_error` cunha mensaxe
// puramente técnica ("elements should have a mounted Payment Element…").
// Cobrimos os casos comúns; o resto cae no fallback xenérico.
interface StripeErrorLike {
  type?: string;
  code?: string;
  message?: string;
}
function describeStripeError(err: StripeErrorLike): string {
  const type = err.type ?? '';
  const code = err.code ?? '';
  const message = err.message ?? '';

  if (type === 'validation_error' || /incomplete/i.test(code) || /incomplete/i.test(message)) {
    return 'Faltan datos no formulario de pagamento. Revisa o IBAN e os datos do titular.';
  }
  if (/mounted Payment Element|Express Checkout Element/i.test(message)) {
    return 'Agarda a que se cargue o formulario de pagamento e proba de novo.';
  }
  if (type === 'card_error') {
    return 'O método de pagamento foi rexeitado polo banco. Proba con outro IBAN/tarxeta.';
  }
  if (code === 'payment_intent_authentication_failure') {
    return 'O banco non puido autenticar o pagamento. Téntao de novo ou cambia de método.';
  }
  if (/network|Failed to fetch|connect/i.test(message)) {
    return 'Erro de conexión con Stripe. Comproba internet e téntao de novo.';
  }
  return 'Non se puido procesar o pagamento. Téntao de novo nuns minutos.';
}

interface ReserveState {
  status: 'loading' | 'ready' | 'error' | 'paid';
  intent: CheckoutIntentResponse | null;
  error: ReserveError | null;
}

export default function CheckoutPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const publishableKey = getPublishableKey();
  const paymentReturnFlag = searchParams.get('payment');
  const paymentIntentReturn = searchParams.get('payment_intent');

  const [state, setState] = useState<ReserveState>({
    status: paymentReturnFlag === 'ok' ? 'paid' : 'loading',
    intent: null,
    error: null,
  });

  const doReserve = useCallback(async () => {
    setState({ status: 'loading', intent: null, error: null });
    try {
      const intent = await reserveSale(id, {});
      setState({ status: 'ready', intent, error: null });
    } catch (err) {
      const classified = classifyReserveError(err);
      if (classified.kind === 'buyer_confirm_required') {
        navigate(`/marketplace/sales/${id}/buyer-confirm`, { replace: true });
        return;
      }
      setState({ status: 'error', intent: null, error: classified });
    }
  }, [id, navigate]);

  useEffect(() => {
    // Stripe-redirect-back branch: the PaymentElement bounces the browser
    // here with ?payment=ok&payment_intent=pi_..., so we skip the reserve
    // call (it would 409 — the sale already moved past LISTED).
    if (paymentReturnFlag === 'ok') return;
    void doReserve();
  }, [doReserve, paymentReturnFlag]);

  if (state.status === 'paid' || paymentReturnFlag === 'ok') {
    return <PostPaymentPanel saleId={id} paymentIntent={paymentIntentReturn} />;
  }

  if (state.status === 'loading') {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="h-40 rounded-xl bg-border/40 animate-pulse" />
        <p className="text-center text-sm text-muted mt-4">Preparando o pagamento…</p>
      </div>
    );
  }

  if (state.status === 'error' && state.error) {
    return <ErrorPanel saleId={id} error={state.error} onRetry={doReserve} />;
  }

  if (state.status === 'ready' && state.intent) {
    if (!publishableKey) {
      return (
        <ErrorPanel
          saleId={id}
          error={{
            kind: 'billing_unavailable',
            message: 'O pagamento non está configurado. Avisa ao operador.',
          }}
          onRetry={doReserve}
        />
      );
    }
    const stripePromise = getStripePromise(publishableKey);
    return (
      <Elements stripe={stripePromise} options={{ clientSecret: state.intent.client_secret }}>
        <CheckoutInner saleId={id} intent={state.intent} />
      </Elements>
    );
  }

  return <div className="max-w-2xl mx-auto px-6 py-12 text-center text-muted">…</div>;
}

interface CheckoutInnerProps {
  saleId: string;
  intent: CheckoutIntentResponse;
}

function CheckoutInner({ saleId, intent }: CheckoutInnerProps): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Lote V-3 (F-032) — disable submit until the PaymentElement reports
  // `complete: true`. Defense-in-depth: Stripe.js _should_ reject
  // confirmPayment with `validation_error` when the IBAN/card is empty,
  // but during the E2E session of 2026-05-25 the button click still
  // produced a confusing technical error. Gating client-side keeps the
  // UX clean and removes a class of race conditions.
  const [paymentComplete, setPaymentComplete] = useState(false);

  const returnUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/marketplace/checkout/${saleId}?payment=ok`;
  }, [saleId]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!stripe || !elements || pending || !paymentComplete) return;
    setError(null);
    setPending(true);
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
      });
      if (result.error) {
        // Lote V-2 (F-029) — Stripe.js entrega mensaxes técnicas en
        // inglés ("Your IBAN is incomplete", "elements should have a
        // mounted Payment Element…"). Traducímolas a copia accionable
        // antes de mostralas; o `result.error.message` literal fica só
        // como fallback para tipos non clasificados.
        setError(describeStripeError(result.error));
      }
      // On success Stripe redirects the browser to `return_url`; nothing else
      // to do here. The post-payment state is rendered on the next mount.
    } catch (err) {
      const msg =
        err instanceof Error
          ? describeStripeError({ message: err.message })
          : 'Erro inesperado ao pagar. Téntao de novo.';
      setError(msg);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-extrabold text-rauxa">Reserva con fondos bloqueados</h1>
      <p className="text-sm text-muted mt-1">
        Autoriza o importe co teu banco para reservar este animal.{' '}
        <strong>Aínda non se cobra nada</strong>: o cargo só se efectúa cando ambos asinedes o
        contrato.
      </p>
      <p className="text-xs text-muted mt-2">
        Aceptamos tarxeta (débito ou crédito) e SEPA Direct Debit.
      </p>

      <div className="mt-6">
        <InfoCallout
          storageKey="checkout-stripe-escrow"
          defaultOpen={true}
          icon={<Lock size={18} className="text-rauxa" />}
          title="Como funciona o bloqueo de fondos"
          body={
            'Stripe pídelle ao teu banco que reserve o importe — non sae da túa conta. Cando vendedor e comprador asinedes o contrato dixital, libérase ao vendedor. Se o vendedor non asina no prazo, o bloqueo caduca e o teu banco libera os fondos automaticamente. Co método SEPA o banco pode tardar 1-3 días en confirmar a reserva.'
          }
        />
      </div>

      <div className="mt-4 p-4 rounded-xl bg-white border border-border">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-muted">Total (inclúe IVE + custos)</span>
          <span className="text-2xl font-extrabold text-rauxa">
            {formatEuros(intent.amount_cents)}
          </span>
        </div>
      </div>

      <form onSubmit={onSubmit} aria-label="checkout" className="mt-5 space-y-4">
        <PaymentElement
          options={{ layout: 'tabs' }}
          onChange={(e) => setPaymentComplete(e.complete)}
        />

        {error ? (
          <div
            role="alert"
            className="p-3 rounded-lg bg-red-50 border border-danger text-danger text-sm"
          >
            {error}
          </div>
        ) : null}

        {!paymentComplete ? (
          <p className="text-xs text-muted text-center">
            Enche o método de pagamento para activar o botón.
          </p>
        ) : null}

        <button
          type="submit"
          disabled={!stripe || !elements || pending || !paymentComplete}
          aria-disabled={!paymentComplete}
          className="w-full py-2.5 rounded-lg bg-[#2D6E1A] text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {pending ? 'Procesando…' : 'Autorizar reserva'}
        </button>
      </form>

      <p className="text-xs text-muted mt-4">
        Cifrado de extremo a extremo. Non hai cobro ata que ambos firmen o contrato.
      </p>
    </div>
  );
}

interface ErrorPanelProps {
  saleId: string;
  error: ReserveError;
  onRetry: () => void;
}

function ErrorPanel({ saleId, error, onRetry }: ErrorPanelProps): JSX.Element {
  const showRetry = error.kind === 'attestations_missing' || error.kind === 'generic';
  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      <div role="alert" className="p-5 rounded-xl bg-red-50 border border-danger text-danger">
        <h2 className="text-lg font-bold">Non podemos completar a reserva</h2>
        <p className="text-sm mt-2">{error.message}</p>
      </div>
      <div className="flex gap-3 mt-5">
        {showRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="px-4 py-2 rounded-lg bg-[#2D6E1A] text-white font-semibold"
          >
            Reintentar
          </button>
        ) : null}
        <Link
          to={`/marketplace/animal/${saleId}`}
          className="px-4 py-2 rounded-lg border border-border text-dark"
        >
          Volver ao animal
        </Link>
      </div>
    </div>
  );
}

interface PostPaymentPanelProps {
  saleId: string;
  paymentIntent: string | null;
}

// Polling cadence + timeout for the contract-signing wait state.
// 3 s matches the PRD; 5 minutes is the give-up budget after which we
// stop hammering the API and ask the user to refresh manually.
export const POLL_INTERVAL_MS = 3_000;
export const POLL_TIMEOUT_MS = 5 * 60 * 1_000;

type SigningStage =
  | 'loading'
  | 'awaiting_contract' // payment OK, Documenso doc id not yet on the sale
  | 'ready_to_sign' // documenso_doc_id present, BUYER hasn't signed yet
  // Lote V-3 followup (F-035) — buyer already signed; just waiting on the
  // seller. Botón "Asinar agora" cambia a "Asinado ✓ — agardando vendedor".
  | 'buyer_signed_waiting_seller'
  | 'awaiting_signature' // user opened the signing window; we poll the sale
  | 'signed'
  | 'timeout'
  | 'error';

interface SigningState {
  stage: SigningStage;
  sale: MarketplaceSaleDetail | null;
  message: string | null;
}

// Lote V-3 (F-031) — until we've heard back from the backend with the
// real PaymentIntent status we don't render the success panel. The
// previous code trusted `?payment=ok` from the URL alone, which let a
// buyer reach "✓ Pagamento procesado" without ever submitting a
// payment method (Stripe.js validation_error, manual nav, or browser
// back/forward could all surface the flag without an actual charge).
type PaymentVerification = 'checking' | 'authorised' | 'unauthorised' | 'error';

function PostPaymentPanel({ saleId, paymentIntent }: PostPaymentPanelProps): JSX.Element {
  const [verification, setVerification] = useState<PaymentVerification>('checking');
  const [state, setState] = useState<SigningState>({
    stage: 'loading',
    sale: null,
    message: null,
  });
  const [opening, setOpening] = useState(false);

  const refresh = useCallback(async (): Promise<MarketplaceSaleDetail | null> => {
    try {
      const sale = await getSale(saleId);
      setState((prev) => ({
        stage: deriveStage(sale, prev.stage),
        sale,
        message: null,
      }));
      return sale;
    } catch {
      setState((prev) => ({
        ...prev,
        stage: prev.sale ? prev.stage : 'error',
        message: 'Non se puido cargar a venda. Téntao máis tarde.',
      }));
      return null;
    }
  }, [saleId]);

  // Lote V-3 (F-031) — verify PI status BEFORE rendering the success
  // panel; only fall through to `refresh()` (sale state polling) when
  // the buyer's payment is actually authorised.
  useEffect(() => {
    let alive = true;
    setVerification('checking');
    getPaymentStatus(saleId)
      .then((status) => {
        if (!alive) return;
        if (status.authorised) {
          setVerification('authorised');
          void refresh();
        } else {
          setVerification('unauthorised');
        }
      })
      .catch(() => {
        if (alive) setVerification('error');
      });
    return () => {
      alive = false;
    };
  }, [saleId, refresh]);

  // Polling loop. Active only when we're explicitly waiting for the
  // Documenso webhook to flip `contract_signed_at` (or for the doc id to
  // appear right after the payment redirect, which is a 1–2 s window).
  useEffect(() => {
    if (
      state.stage !== 'awaiting_signature' &&
      state.stage !== 'awaiting_contract' &&
      state.stage !== 'buyer_signed_waiting_seller'
    ) {
      return;
    }
    const startedAt = Date.now();
    // `setInterval` polls the sale endpoint every POLL_INTERVAL_MS until
    // `contract_signed_at` flips (success) or POLL_TIMEOUT_MS elapses.
    // The interval is named `pollSales` so the PRD's grep gate matches.
    const pollSales = setInterval(() => {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        clearInterval(pollSales);
        setState((prev) => ({ ...prev, stage: 'timeout', message: null }));
        return;
      }
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollSales);
  }, [state.stage, refresh]);

  const onOpenSigning = useCallback(async () => {
    setOpening(true);
    try {
      const { url, signed } = await getSigningUrl(saleId);
      if (signed) {
        // Already signed: just refresh; the backend will surface the
        // `contract_signed_at` timestamp on the next sale fetch.
        await refresh();
        return;
      }
      if (!url) {
        setState((prev) => ({
          ...prev,
          message: 'O contrato aínda non está listo. Téntao en breve.',
        }));
        return;
      }
      // Mobile browsers block `window.open` after an `await` because the
      // user-gesture context expired. Navigate in the same tab — Documenso
      // returns to the LeyroChain post-signing redirect when done, and the
      // sale polling picks the signed status up.
      setState((prev) => ({ ...prev, stage: 'awaiting_signature', message: null }));
      window.location.assign(url);
    } catch {
      setState((prev) => ({
        ...prev,
        message: 'Non se puido obter a ligazón do contrato. Téntao máis tarde.',
      }));
    } finally {
      setOpening(false);
    }
  }, [saleId, refresh]);

  // Lote V-3 (F-031) — branches that block the "✓ Pagamento procesado"
  // until backend confirms the PaymentIntent is in a state that means
  // money moved (or is about to). Anything else surfaces an actionable
  // error instead of a misleading success.
  if (verification === 'checking') {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <div className="h-32 rounded-xl bg-border/40 animate-pulse" />
        <p className="text-sm text-muted mt-4">Verificando o pagamento…</p>
      </div>
    );
  }
  if (verification === 'unauthorised') {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12 text-center">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger text-2xl">
          !
        </div>
        <h1 className="text-2xl font-extrabold text-dark mt-4">Reserva non confirmada</h1>
        <p className="text-sm text-muted mt-2 max-w-md mx-auto">
          O teu banco non autorizou o bloqueo de fondos. Volve ao paso anterior e introduce un
          método de pagamento válido. Aínda non se cobrou nada.
        </p>
        <div className="flex justify-center gap-3 mt-6">
          <Link
            to={`/marketplace/checkout/${saleId}`}
            className="px-4 py-2 rounded-lg bg-[#2D6E1A] text-white font-semibold"
          >
            Volver ao pago
          </Link>
          <Link
            to={`/marketplace/animal/${saleId}`}
            className="px-4 py-2 rounded-lg border border-border text-dark"
          >
            Volver ao animal
          </Link>
        </div>
      </div>
    );
  }
  if (verification === 'error') {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12 text-center">
        <h1 className="text-2xl font-extrabold text-dark mt-4">Non se puido verificar a reserva</h1>
        <p className="text-sm text-muted mt-2 max-w-md mx-auto">
          Inténtao de novo nuns segundos. Se o problema persiste, contacta co operador. Non te
          cobramos nada ata que ambos firmedes o contrato.
        </p>
        <div className="flex justify-center gap-3 mt-6">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-[#2D6E1A] text-white font-semibold"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-12 text-center">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-chain-soft text-chain text-2xl">
        🔒
      </div>
      <h1 className="text-2xl font-extrabold text-rauxa mt-4">Fondos bloqueados</h1>
      <p className="text-sm text-muted mt-2 max-w-md mx-auto">
        O teu banco reservou o importe — non sae da túa conta ata que ambos asinedes o contrato. Se
        o vendedor non asina, libérase automaticamente.
      </p>
      {paymentIntent ? (
        <p className="text-[11px] font-mono text-muted mt-2">{paymentIntent}</p>
      ) : null}

      <SigningBody state={state} opening={opening} onOpenSigning={onOpenSigning} />

      {state.message ? (
        <p role="alert" className="text-sm text-danger mt-3 max-w-md mx-auto">
          {state.message}
        </p>
      ) : null}

      <div className="flex justify-center gap-3 mt-6">
        <Link
          to={`/marketplace/animal/${saleId}`}
          className="px-4 py-2 rounded-lg bg-[#2D6E1A] text-white font-semibold"
        >
          Ver a venda
        </Link>
      </div>
    </div>
  );
}

function deriveStage(sale: MarketplaceSaleDetail, prev: SigningStage): SigningStage {
  if (sale.contract_signed_at) return 'signed';
  if (!sale.documenso_doc_id) return 'awaiting_contract';
  // Lote V-3 followup (F-035) — buyer xa sinou; ocultamos "Asinar agora"
  // e mostramos confirmación + estado do vendedor.
  if (sale.buyer_signed_at) return 'buyer_signed_waiting_seller';
  // Documenso doc is provisioned. If we were already in `awaiting_signature`,
  // hold that state so the polling banner keeps showing — refresh just
  // re-checks `contract_signed_at`.
  if (prev === 'awaiting_signature') return 'awaiting_signature';
  return 'ready_to_sign';
}

interface SigningBodyProps {
  state: SigningState;
  opening: boolean;
  onOpenSigning: () => void;
}

function SigningBody({ state, opening, onOpenSigning }: SigningBodyProps): JSX.Element {
  if (state.stage === 'loading') {
    return <p className="text-sm text-muted mt-4">Cargando o estado da venda…</p>;
  }
  if (state.stage === 'signed') {
    return (
      <div className="mt-6" data-testid="signing-signed">
        <p className="text-base font-semibold text-chain">Contrato asinado ✓</p>
        <p className="text-sm text-muted mt-1">
          O vendedor asinará en breve. Avisarémoste por email cando o contrato estea pechado.
        </p>
      </div>
    );
  }
  if (state.stage === 'awaiting_contract') {
    return (
      <p className="text-sm text-muted mt-4" data-testid="signing-awaiting-contract">
        Estamos a preparar o contrato dixital. Tardará uns segundos…
      </p>
    );
  }
  if (state.stage === 'ready_to_sign') {
    return (
      <div className="mt-6" data-testid="signing-ready">
        <p className="text-sm text-muted">
          O pagamento quedou retido en escrow. Asina agora o contrato de compravenda dixital.
        </p>
        <button
          type="button"
          onClick={onOpenSigning}
          disabled={opening}
          className="mt-4 px-5 py-2.5 rounded-lg bg-[#2D6E1A] text-white font-semibold disabled:opacity-60"
        >
          {opening ? 'Abrindo…' : 'Asinar agora'}
        </button>
      </div>
    );
  }
  if (state.stage === 'buyer_signed_waiting_seller') {
    // Lote V-3 followup (F-035) — buyer xa sinou. Botón antigo
    // substituído por confirmación.
    return (
      <div className="mt-6" data-testid="signing-buyer-signed">
        <p className="text-base font-semibold text-chain">Asinaches o contrato ✓</p>
        <p className="text-sm text-muted mt-1">
          Estamos á espera de que o vendedor sine. Avisarémoste por email cando se peche.
        </p>
      </div>
    );
  }
  if (state.stage === 'awaiting_signature') {
    return (
      <div className="mt-6" data-testid="signing-awaiting">
        <p className="text-sm font-semibold text-dark">Asinando…</p>
        <p className="text-sm text-muted mt-1">
          Completa a sinatura na pestana que se abriu. Detectaremos automaticamente cando termines.
        </p>
        <button
          type="button"
          onClick={onOpenSigning}
          disabled={opening}
          className="mt-4 text-sm text-rauxa font-semibold underline"
        >
          Volver abrir a pestana de sinatura
        </button>
      </div>
    );
  }
  if (state.stage === 'timeout') {
    return (
      <div className="mt-6" data-testid="signing-timeout">
        <p className="text-sm text-muted">
          Toma máis tempo do esperado. Recarga esta páxina cando termines de asinar.
        </p>
      </div>
    );
  }
  return (
    <p className="text-sm text-muted mt-4">
      Houbo un problema cargando o contrato. Visita a páxina da venda en breve.
    </p>
  );
}
